import { baseNameFor, disambiguate } from './naming'

/**
 * Saved releases, in IndexedDB.
 *
 * Treat this as a cache, not as the record of account. A browser can clear its
 * storage under pressure or on a user's say-so, which is fine for a PDF that has
 * already been shared out and unacceptable for one that has not. The finish
 * screen therefore pushes you to send a copy immediately.
 */

const DB_NAME = 'consent'
const DB_VERSION = 1
const STORE = 'releases'

export interface Submission {
  id: string
  templateId: string
  templateTitle: string
  templateVersion: number
  signerName: string
  signerEmail: string
  values: Record<string, string>
  signedAtMillis: number
  signedAtIso: string
  timeZone: string
  device: string
  appVersion: string
  readToEnd: boolean
  fingerprint: string
  pdfFileName: string
  pageCount: number
  /** The signed PDF itself. */
  pdf: Blob
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('signedAtMillis', 'signedAtMillis')
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Could not open local storage.'))
  })
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode)
        const request = run(transaction.objectStore(STORE))
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error('Local storage rejected the write.'))
        transaction.oncomplete = () => db.close()
      }),
  )
}

export async function saveRelease(submission: Submission): Promise<void> {
  await tx('readwrite', (store) => store.put(submission))
}

/** Newest first. */
export async function listReleases(): Promise<Submission[]> {
  const all = await tx<Submission[]>('readonly', (store) => store.getAll() as IDBRequest<Submission[]>)
  return all.sort((a, b) => b.signedAtMillis - a.signedAtMillis)
}

export async function getRelease(id: string): Promise<Submission | undefined> {
  return tx<Submission | undefined>('readonly', (store) => store.get(id) as IDBRequest<Submission | undefined>)
}

export async function deleteRelease(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id))
}

export async function nextFileName(signerName: string, date: Date): Promise<string> {
  const existing = await listReleases()
  return disambiguate(baseNameFor(signerName, date), new Set(existing.map((r) => r.pdfFileName)))
}

/**
 * Asks the browser to keep this origin's storage rather than evicting it under
 * pressure. Granted silently on an installed PWA in most browsers; a refusal is
 * not an error, just a reason to get copies off the device promptly.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  try {
    if (await navigator.storage.persisted()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}
